import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';

export const ProductGallery = ({ images = [], productName = '' }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Handle case where images might be a single string (backward compatibility)
  const imageArray = Array.isArray(images) ? images : images ? [images] : [];
  
  if (imageArray.length === 0) {
    return (
      <div className="w-full h-56 bg-gray-100 flex items-center justify-center">
        <span className="text-gray-400">No image available</span>
      </div>
    );
  }

  const currentImage = imageArray[selectedIndex];
  const hasPrevious = selectedIndex > 0;
  const hasNext = selectedIndex < imageArray.length - 1;

  const goToPrevious = () => {
    if (hasPrevious) setSelectedIndex(selectedIndex - 1);
  };

  const goToNext = () => {
    if (hasNext) setSelectedIndex(selectedIndex + 1);
  };

  return (
    <div className="space-y-3">
      {/* Main Image */}
      <div className="relative h-56 overflow-hidden bg-white rounded-t-lg group flex items-center justify-center">
        <img
          src={currentImage}
          alt={`${productName} - Image ${selectedIndex + 1}`}
          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
        />
        
        {/* Navigation Arrows (only show if multiple images) */}
        {imageArray.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPrevious}
              disabled={!hasPrevious}
              className={`absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white ${
                !hasPrevious ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
              } transition-opacity`}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNext}
              disabled={!hasNext}
              className={`absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white ${
                !hasNext ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
              } transition-opacity`}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </>
        )}

        {/* Image Counter */}
        {imageArray.length > 1 && (
          <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
            {selectedIndex + 1} / {imageArray.length}
          </div>
        )}
      </div>

      {/* Thumbnail Strip (only show if multiple images) */}
      {imageArray.length > 1 && (
        <div className="flex gap-2 px-3 pb-3 overflow-x-auto">
          {imageArray.map((img, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all bg-white flex items-center justify-center ${
                index === selectedIndex
                  ? 'border-[#61525a] ring-2 ring-[#61525a]/30'
                  : 'border-transparent hover:border-[#61525a]/50'
              }`}
            >
              <img
                src={img}
                alt={`${productName} thumbnail ${index + 1}`}
                className="w-full h-full object-contain"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
